import { useEffect, useState } from 'react';
import { useForm } from '@abyss/web/hooks/useForm';
import { useRouter } from '@abyss/web/hooks/useRouter';
import { PageScrollService } from '@src/api/common/PageScrollService';
import { AlertService } from '@src/api/common/AlertService';
import { RedirectService } from '@src/api/common/RedirectService';
import { AlertVariantType } from '@src/constants/AlertVariantType';
import { EmployeeService } from '@src/api/web-services/SampleService/EmployeeService';
import { PageTitleService } from '@src/api/common/PageTitleService';
import { BreadcrumbService } from '@src/api/common/BreadcrumbService';
import { PageTitle } from '@src/api/common/PageTitleType';
import { BreadcrumbItem } from '@src/api/common/BreadcrumbTypes';
import { Breadcrumbs, Title } from './PageInfo';

export const EmployeeDetailController = () => {
  type Data = {
    item: any;
    detailResponse: any;
  };
  const [pageData, setPageData] = useState<Data>({
    item: {},
    detailResponse: null,
  });

  const { getRouteParams } = useRouter();
  const params = getRouteParams('');
  const { ScrollToTop, pageTop } = PageScrollService();
  const { AlertModel, ShowAlert } = AlertService();
  const { Redirect, RedirectDuration } = RedirectService();
  const { UpdatePageTitle } = PageTitleService();
  const { UpdateBreadcrumb } = BreadcrumbService();

  const {
    GetEmployeeById,
    CreateOrUpdateEmployeeById,
    LogicDeleteEmployeeById,
  } = EmployeeService();

  const form = useForm({
    defaultValues: {
      id: 0,
      name: '',
      email: '',
      age: 18,
      department: '',
      position: '',
      salary: 0,
      createdDate: '',
      createdBy: '',
      updatedDate: '',
      updatedBy: '',
    },
    values: pageData.item,
  });

  const DataSource = {
    GetDetail: async () => {
      if (!params?.id || params.id === '0') {
        return;
      }
      const body = {
        id: ~~params.id,
      };
      const apiData: any = await GetEmployeeById(body);
      await setPageData((pageData) => ({
        ...pageData,
        detailResponse: apiData,
      }));
      if (apiData.status === 'success' && apiData.data) {
        let data = apiData.data;
        setPageData((pageData) => ({
          ...pageData,
          item: data,
        }));
      } else if (apiData.data?.message) {
        await form.reset();
        AlertModel.variant = AlertVariantType.ERROR;
        AlertModel.message = apiData.data?.message;
        AlertModel.visible = true;
        AlertModel.isPermanent = true;
        await ShowAlert(AlertModel);
      }
    },
    DeleteItem: async () => {
      if (params?.id) {
        const body = {
          id: ~~params.id,
        };
        const apiData: any = await LogicDeleteEmployeeById(body);
        if (apiData.message) {
          AlertModel.variant =
            apiData.status === 'success'
              ? AlertVariantType.SUCCESS
              : AlertVariantType.ERROR;
          AlertModel.message = apiData.message;
          AlertModel.visible = true;
          await ShowAlert(AlertModel);
          if (apiData?.status === 'success') {
            form.reset();
            Action.GoBackToList();
          }
        }
      }
    },
    PostDetail: async () => {
      const body = form.getValues();
      const apiData: any = await CreateOrUpdateEmployeeById(body);
      if (apiData?.status === 'success' && apiData?.data) {
        let data = apiData.data;
        await setPageData((pageData) => ({
          ...pageData,
          item: data,
        }));
        if (data.id > 0) {
          AlertModel.variant = AlertVariantType.SUCCESS;
          AlertModel.message = 'Employee saved successfully';
          AlertModel.visible = true;
          await ShowAlert(AlertModel);
          Action.GoBackToList();
        }
      } else {
        AlertModel.variant = AlertVariantType.ERROR;
        AlertModel.message = 'Failed to save employee';
        AlertModel.visible = true;
        await ShowAlert(AlertModel);
      }
    },
  };

  const Action = {
    InIt: async () => {
      await UpdatePageTitle(Title);
      await UpdateBreadcrumb(Breadcrumbs);
      Action.Load();
    },
    GoBackToList: () => {
      Redirect(`/datatable-example`, RedirectDuration.SHORT);
    },
    Load: async () => {
      await DataSource.GetDetail();
    },
    OnDeleteConfirm: async () => {
      await DataSource.DeleteItem();
      await ScrollToTop();
    },
    Save: async () => {
      await DataSource.PostDetail();
      await ScrollToTop();
    },
  };

  useEffect(() => {
    Action.InIt();
  }, []);

  return {
    form,
    Action,
    pageTop,
    params,
    pageData,
  };
};
